import { useMemo, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import {
  NotificationTemplateTypes,
  notificationTemplatesSettingsTree,
  getPrepareNotificationsSettings,
  PermissionEnum,
} from '@ui/src';
import {
  onChangeHelperArray,
  onChangeHelperSingle,
} from '../notification-settings/helpers/on-change-helper';
import {
  onSectionChange,
  onPageChange,
  handleConfirmPopup,
  onSearch,
} from './store/slices/notification-settings';
import { useEditUserNotificationTypesMutation } from './store/services/users';
import { useLazyGetNotificationsTemplatesTypesQuery } from '../notification-settings/store/services';
import ColumnWithToggle from '../../components/notification-settings/column-with-toggle';
import ColumnWithConfirmHOC from './column-with-confirm';

import cn from './styles.module.css';
import sectionActions from '../notification-settings/helpers/get-section-actions';

const NotificationSettingsColumn = () => {
  const dispatch = useAppDispatch();

  const {
    userList: {
      users: { selectedUser, userId },
      notificationSettings: {
        sectionId,
        pageId,
        search,
        allActionsSectionFiltered,
      },
    },
    administrationDictionaries: { activePermissions },
    notificationSettings: {
      templates: { templateTypes },
    },
  } = useAppSelector((state) => state);

  const [values, setValues] = useState(selectedUser?.notificationsTypes || []);

  const typesEnumKeys = Object.keys(NotificationTemplateTypes).map(
    (item) => item,
  );
  const unassignedTypes = templateTypes?.filter((item) =>
    typesEnumKeys.every((type) => item !== type),
  );

  let nextNotificationTeplatesSettingsTree = notificationTemplatesSettingsTree;

  if (unassignedTypes?.length) {
    nextNotificationTeplatesSettingsTree = [
      ...nextNotificationTeplatesSettingsTree,
      {
        key: 'SECTION_UNASSIGNED' as NotificationTemplateTypes,
        pages: [
          {
            key: 'UNASSIGNED_LIST' as NotificationTemplateTypes,
            actions: unassignedTypes
              .filter((item) => item.includes('_ACTION'))
              .map((item) => ({
                key: item,
              })),
          },
        ],
      },
    ];
  }

  const columnsData = getPrepareNotificationsSettings(
    nextNotificationTeplatesSettingsTree,
  );

  const [editTypes, { isLoading, isSuccess }] =
    useEditUserNotificationTypesMutation();
  const [getNotificationsTemplatesTypes] =
    useLazyGetNotificationsTemplatesTypesQuery();

  const sections = userId ? columnsData : undefined;
  const pages = userId && sectionId ? columnsData[sectionId]?.pages : undefined;
  const actions =
    userId && sectionId && pageId && pageId !== 'all'
      ? columnsData[sectionId]?.pages?.[pageId]?.actions
      : undefined;

  const activeSections = useMemo(
    () => values.filter((value) => value.includes('SECTION')),
    [values],
  );

  const activePages = useMemo(
    () =>
      values.filter(
        (value) => !value.includes('SECTION') && !value.includes('_ACTION'),
      ),
    [values],
  );

  const activeActions = useMemo(
    () => values.filter((value) => value.includes('_ACTION')),
    [values],
  );

  const allActionsSection = sectionId ? sectionActions[sectionId] : undefined;

  const onChange = (
    key: NotificationTemplateTypes | NotificationTemplateTypes[],
    value: boolean,
    columnsDataItem: any,
  ) => {
    if (Array.isArray(key)) {
      onChangeHelperArray(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        pageId,
      );
    } else {
      onChangeHelperSingle(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        activeActions,
      );
    }
  };

  const onSearchSettings = (value: string) => {
    dispatch(onSearch(value));
  };

  const onSubmit = async () => {
    const availableEnums = values
      ?.filter((item) => item.includes('_ACTION'))
      .map((item) => item.replace('_ACTION', ''));

    await editTypes({
      id: selectedUser?.id as string,
      body: {
        notificationsTypes: availableEnums as NotificationTemplateTypes[],
      },
    });
  };

  const onSelectRow = (
    key: NotificationTemplateTypes | 'all',
    name: 'sectionId' | 'pageId',
  ) => {
    if (name === 'sectionId') {
      dispatch(onSectionChange(key as NotificationTemplateTypes));
    } else if (name === 'pageId') {
      dispatch(onPageChange(key as NotificationTemplateTypes));
    }
  };

  const onCancel = () => {
    dispatch(handleConfirmPopup(false));
    setValues(selectedUser?.notificationsTypes || []);
  };

  useEffect(() => {
    if (selectedUser?.notificationsTypes) {
      setValues(selectedUser?.notificationsTypes);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!pageId && sections) {
      onSelectRow(
        sections[sectionId]?.pagesKeys[0] as NotificationTemplateTypes,
        'pageId',
      );

      dispatch(onSectionChange(sectionId));
    }
  }, [sectionId, sections]);

  useEffect(() => {
    if (selectedUser && selectedUser.notificationsTypes && values) {
      const availableEnums = values?.filter((item) => item.includes('_ACTION'));

      const isDirty =
        selectedUser.notificationsTypes?.filter((item) =>
          availableEnums.every((value) => item !== value),
        )?.length > 0 ||
        availableEnums?.filter((item) =>
          selectedUser.notificationsTypes?.every(
            (permission) => item !== permission,
          ),
        )?.length;

      if (isDirty) {
        dispatch(handleConfirmPopup(true));
      } else {
        dispatch(handleConfirmPopup(false));
      }
    }
  }, [selectedUser, values]);

  useEffect(() => {
    if (selectedUser?.notificationsTypes) {
      const notificationsTypes = ([] as NotificationTemplateTypes[]).concat(
        selectedUser?.notificationsTypes,
      );

      Object.keys(columnsData).forEach((key) => {
        if ((columnsData[key] as any) && (columnsData[key] as any).pages) {
          Object.keys((columnsData[key] as any).pages).forEach((page) => {
            if (
              columnsData &&
              (columnsData[key] as any) &&
              (columnsData[key] as any).pages &&
              (columnsData[key] as any).pages[page]
            ) {
              const actionsValues: NotificationTemplateTypes[] = (
                columnsData[key] as any
              ).pages[page]?.actionsKeys;

              actionsValues.forEach((action) => {
                if (
                  notificationsTypes?.includes(action) &&
                  !notificationsTypes.includes(
                    page as NotificationTemplateTypes,
                  )
                ) {
                  notificationsTypes.push(page as NotificationTemplateTypes);
                }
              });

              actionsValues.forEach((action) => {
                if (
                  notificationsTypes?.includes(action) &&
                  !notificationsTypes.includes(key as NotificationTemplateTypes)
                ) {
                  notificationsTypes.push(key as NotificationTemplateTypes);
                }
              });
            }
          });
        }
      });

      setValues(notificationsTypes || []);
    }
  }, [selectedUser?.notificationsTypes]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.NOTIFICATIONS_TYPES_TEMPLATE_LIST,
      )
    ) {
      getNotificationsTemplatesTypes();
    }
  }, [activePermissions]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(handleConfirmPopup(false));
    }
  }, [isSuccess]);

  return (
    <ColumnWithConfirmHOC
      onSubmit={onSubmit}
      onCancel={onCancel}
      editIsLoading={isLoading}
    >
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeSections}
          columnData={sections}
          onChange={onChange}
          onSelectRow={(key: NotificationTemplateTypes | 'all') =>
            onSelectRow(key, 'sectionId')
          }
          selectedId={sectionId}
          hasAllToggleButton={false}
          title="Notification Category"
          emptyTitle="—"
          disabled={
            !activePermissions?.includes(
              PermissionEnum.USERS_EDIT_NOTIFICATIONS_TYPES_ACTION,
            )
          }
          isEditable
        />
      </div>
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activePages}
          columnData={pages}
          onChange={onChange}
          onSelectRow={(key: NotificationTemplateTypes | 'all') =>
            onSelectRow(key, 'pageId')
          }
          selectedId={pageId}
          title="Notification Group"
          disabled={
            !activePermissions?.includes(
              PermissionEnum.USERS_EDIT_NOTIFICATIONS_TYPES_ACTION,
            )
          }
          allActionsSectionButton={Boolean(allActionsSection)}
          emptyTitle="—"
          isEditable
        />
      </div>
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeActions}
          columnData={pageId !== 'all' ? actions : allActionsSectionFiltered}
          onChange={onChange}
          selectedId={pageId}
          title="Notification Type"
          emptyTitle="View only"
          onSearch={onSearchSettings}
          disabled={
            !activePermissions?.includes(
              PermissionEnum.USERS_EDIT_NOTIFICATIONS_TYPES_ACTION,
            )
          }
          search={search}
          isEditable
        />
      </div>
    </ColumnWithConfirmHOC>
  );
};

export default NotificationSettingsColumn;
